import {
    Directive, ElementRef, Output, EventEmitter,
    OnDestroy, AfterViewInit, Input, OnChanges,
    SimpleChanges
} from '@angular/core';

@Directive({
    selector: '[clickOutside]',
})
export class ClickOutsideDirective implements OnDestroy, AfterViewInit, OnChanges {
    @Input()
    public escape: boolean = false;

    @Output()
    public readonly clickOutside = new EventEmitter();

    @Input('clickOutsideEnabled')
    public enabled: boolean = true;


    private timestamps: any = new Set<number>();

    private onClickHandle: any;
    private onClickInsideHandle: any;
    private onKeyboardHandle: any;
    private timeoutHandle: any;

    constructor(private elementRef: ElementRef) {
        // We need to keep a track of this function, because we need to call removeEventListener with the same function
        // without loosing the reference to this
        this.onClickHandle = this.onClick.bind(this);
        this.onClickInsideHandle = this.onClickInside.bind(this);
        this.onKeyboardHandle = this.onKeyboard.bind(this);
    }

    public ngAfterViewInit(): void {
        // Because sometimes elements are displayed by a click
        // In this case, the directive is applied from the click that triggered the element's display
        // This timeout allows the click that displayed the element to be
        // propagated through the DOM without calling clickOutside
        if (this.enabled){
            this.registerTimedOutClick();
        }

        if (this.escape) {
            this.registerEscape();
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.escape && changes.escape.currentValue !== changes.escape.previousValue) {
            if (changes.escape.currentValue) {
                this.registerEscape();
            } else {
                this.unregisterEscape();
            }
        }

        if (changes.enabled && changes.enabled.currentValue !== changes.enabled.previousValue){
            if (changes.enabled.currentValue){
                this.registerTimedOutClick();
            }else{
                this.unregisterClick();
            }
        }
    }

    public ngOnDestroy(): void {
        if (this.timeoutHandle) {
            clearTimeout(this.timeoutHandle);
        }
        this.unregisterClick();
        this.unregisterEscape();
    }


    public onClick(event: MouseEvent): void {
        // Sometimes, the clicked element was inside the watched container
        // but at the time we receive the event, it is already removed from the DOM
        // The only safe way to compare if we clicked an element inside our container
        // is to save their timestamp and compare timestamps
        if (!this.timestamps.has(event.timeStamp)){
            this.clickOutside.emit(event);
        }else{
            this.timestamps.delete(event.timeStamp);
        }
    }

    public onKeyboard($event: KeyboardEvent): void {
        if ($event.which === 27) {
            this.clickOutside.emit(event);
        }
    }

    private registerEscape(): void {
        document.addEventListener("keydown", this.onKeyboardHandle);
    }

    private unregisterEscape(): void {
        document.removeEventListener("keydown", this.onKeyboardHandle);
    }

    private registerTimedOutClick(): void{
        this.timeoutHandle = setTimeout(() => {
            this.registerClick();
        }, 1);
    }

    private registerClick(): void{
        document.addEventListener("click", this.onClickHandle);
        this.elementRef.nativeElement.addEventListener("click", this.onClickInsideHandle);
    }

    private unregisterClick(): void{
        document.removeEventListener("click", this.onClickHandle);
        this.elementRef.nativeElement.removeEventListener("click", this.onClickInsideHandle);
    }

    private onClickInside(event: MouseEvent): void{
        this.timestamps.add(event.timeStamp);
    }

}
